import React, { useState, useEffect } from 'react';
import OnboardingGuide from "./OnboardingGuide";
import MigrationWizard from "./MigrationWizard";
import { useAuth } from '../contexts/AuthContext';
import {
  Activity, Users, Upload, FileText, Trash2, CheckCircle, Clock, AlertCircle,
  BarChart3, Zap, TrendingUp, ArrowUpRight, ArrowDownLeft,
  DollarSign, Eye, RefreshCw
} from 'lucide-react';

const Dashboard = ({ setPage }) => {
  const { token, user } = useAuth();
  const API_URL = process.env.REACT_APP_API_URL || 'https://api.jaychrisedi.com';

  const [summary, setSummary] = useState(null);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showOnboarding, setShowOnboarding] = useState(!localStorage.getItem("edi_onboarded"));
  const [showMigration, setShowMigration] = useState(false);
  const [selectedTx, setSelectedTx] = useState(null);
  const [selectedPartner, setSelectedPartner] = useState(null);

  useEffect(() => {
    if (token) fetchDashboardData();
    const interval = setInterval(() => { if (token) fetchDashboardData(); }, 30000);
    return () => clearInterval(interval);
  }, [token]);

  const fetchDashboardData = async () => {
    try {
      const [summaryRes, partnersRes] = await Promise.all([
        fetch(API_URL + '/api/transactions/summary', { headers: { 'Authorization': 'Bearer ' + token } }),
        fetch(API_URL + '/api/partners', { headers: { 'Authorization': 'Bearer ' + token } })
      ]);
      const summaryData = await summaryRes.json();
      const partnersData = await partnersRes.json();
      setSummary(summaryData);
      setPartners(Array.isArray(partnersData) ? partnersData : []);
    } catch (err) {
      console.error('Dashboard fetch error:', err);
    }
    setLoading(false);
  };

  const getTypeLabel = (type) => {
    const labels = { '850': 'Purchase Order', '810': 'Invoice', '856': 'Ship Notice', '997': 'Acknowledgment' };
    return labels[type] || type;
  };

  const getTypeColor = (type) => {
    const colors = { '850': '#3b82f6', '810': '#10b981', '856': '#f59e0b', '997': '#8b5cf6' };
    return colors[type] || '#64748b';
  };

  const getStatusStyle = (status) => {
    if (status === 'completed') return { bg: '#d1fae5', color: '#065f46', icon: CheckCircle, text: 'Completed' };
    if (status === 'pending') return { bg: '#fef3c7', color: '#92400e', icon: Clock, text: 'Pending' };
    if (status === 'error') return { bg: '#fee2e2', color: '#991b1b', icon: AlertCircle, text: 'Error' };
    return { bg: '#dbeafe', color: '#1e40af', icon: Activity, text: status || 'Unknown' };
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = (now - date) / 1000;
    if (diff < 60) return 'Just now';
    if (diff < 3600) return Math.floor(diff / 60) + ' min ago';
    if (diff < 86400) return Math.floor(diff / 3600) + ' hours ago';
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc' }}>
        <RefreshCw size={32} style={{ animation: 'spin 1s linear infinite', color: '#10b981' }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#F4F6F9', fontFamily: 'Inter, sans-serif' }}>
      {showOnboarding && <OnboardingGuide onClose={() => { setShowOnboarding(false); localStorage.setItem("edi_onboarded", "true"); }} />}
      {showMigration && <MigrationWizard onClose={() => { setShowMigration(false); fetchDashboardData(); }} />}
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #0D2B5E 0%, #071830 100%)',
        padding: '28px 32px', color: 'white',
      }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '11px', fontFamily: 'JetBrains Mono, monospace', color: 'rgba(255,255,255,0.4)', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '6px' }}>
              Overview
            </div>
            <h1 style={{ fontSize: '28px', fontWeight: '800', letterSpacing: '-0.5px', marginBottom: '4px', fontFamily: 'Inter, sans-serif' }}>
              Dashboard
            </h1>
            <p style={{ fontSize: '14px', color: 'rgba(255,255,255,0.55)', fontFamily: 'Inter, sans-serif' }}>
              {partners.length} active trading partner{partners.length !== 1 ? 's' : ''}
            </p>
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button onClick={() => setPage('partner')} style={{
              backgroundColor: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)',
              color: 'rgba(255,255,255,0.85)', padding: '9px 18px', borderRadius: '8px', fontSize: '13px',
              fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px',
              fontFamily: 'Inter, sans-serif',
            }}>
              <Users size={14} /> Add Partner
            </button>
            <button onClick={() => setShowMigration(true)} style={{
              backgroundColor: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)',
              color: 'rgba(255,255,255,0.85)', padding: '9px 18px', borderRadius: '8px', fontSize: '13px',
              fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px',
              fontFamily: 'Inter, sans-serif',
            }}>
              <Upload size={14} /> Import from SPS
            </button>
            <button onClick={fetchDashboardData} style={{
              backgroundColor: '#6DC234', border: 'none',
              color: 'white', padding: '9px 18px', borderRadius: '8px', fontSize: '13px',
              fontWeight: '700', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px',
              fontFamily: 'Inter, sans-serif', boxShadow: '0 2px 8px rgba(109,194,52,0.4)',
            }}>
              <RefreshCw size={14} /> Refresh
            </button>
          </div>
        </div>
      </div>

      <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '24px 16px' }}>
        {/* Stat Cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px', marginBottom: '32px', marginTop: '-60px' }}>
          {[
            { label: 'Total Transactions', value: summary?.total || 0, icon: FileText, color: '#3b82f6', bg: '#dbeafe' },
            { label: 'Completed', value: summary?.completed || 0, icon: CheckCircle, color: '#10b981', bg: '#d1fae5' },
            { label: 'Errors', value: summary?.errors || 0, icon: AlertCircle, color: '#ef4444', bg: '#fee2e2' },
            { label: 'Total Revenue', value: '$' + (summary?.totalRevenue || 0).toLocaleString(), icon: DollarSign, color: '#8b5cf6', bg: '#ede9fe' }
          ].map((stat, i) => (
            <div key={i} style={{
              backgroundColor: 'rgba(255,255,255,0.95)', borderRadius: '16px', padding: '24px',
              boxShadow: '0 4px 6px rgba(0,0,0,0.07)', border: '1px solid rgba(226,232,240,0.8)'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                <span style={{ fontSize: '13px', color: '#64748b', fontWeight: '600' }}>{stat.label}</span>
                <div style={{ backgroundColor: stat.bg, borderRadius: '8px', padding: '6px', display: 'flex' }}>
                  <stat.icon size={16} style={{ color: stat.color }} />
                </div>
              </div>
              <div style={{ fontSize: '32px', fontWeight: '900', color: '#0f172a', letterSpacing: '-1px' }}>{stat.value}</div>
            </div>
          ))}
        </div>

        {/* Active Partners */}
        <div style={{
          backgroundColor: 'rgba(255,255,255,0.95)', borderRadius: '16px', padding: '28px',
          boxShadow: '0 4px 6px rgba(0,0,0,0.07)', border: '1px solid rgba(226,232,240,0.8)', marginBottom: '32px'
        }}>
          <h2 style={{ fontSize: '18px', fontWeight: '800', color: '#0f172a', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Users size={20} style={{ color: '#10b981' }} /> Active Trading Partners
          </h2>
          {partners.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
              <Users size={48} style={{ opacity: 0.3, marginBottom: '12px' }} />
              <p style={{ fontSize: '16px', marginBottom: '16px' }}>No trading partners yet</p>
              <button onClick={() => setPage('partner')} style={{
                background: 'linear-gradient(135deg, #10b981, #059669)', color: 'white', border: 'none',
                padding: '12px 24px', borderRadius: '10px', fontSize: '14px', fontWeight: '700', cursor: 'pointer'
              }}>
                Add Your First Partner
              </button>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px' }}>
              {partners.map(p => (
                <div key={p.id} onClick={() => setSelectedPartner(p)} style={{ cursor: 'pointer',
                  padding: '20px', borderRadius: '12px', border: '2px solid #e2e8f0',
                  display: 'flex', alignItems: 'center', gap: '16px', justifyContent: 'space-between'
                }}>
                  <div style={{
                    width: '48px', height: '48px', borderRadius: '12px',
                    background: 'linear-gradient(135deg, #10b981, #059669)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white',
                    fontSize: '20px', fontWeight: '900', flexShrink: 0
                  }}>
                    {p.name.charAt(0)}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '16px', fontWeight: '700', color: '#0f172a' }}>{p.name}</div>
                    <div style={{ fontSize: '12px', color: '#64748b', fontFamily: 'monospace' }}>{p.ediIdentifier}</div>
                    <div style={{
                      fontSize: '11px', padding: '2px 8px', borderRadius: '4px', marginTop: '4px', display: 'inline-block',
                      backgroundColor: p.status === 'active' ? '#d1fae5' : '#fee2e2',
                      color: p.status === 'active' ? '#065f46' : '#991b1b'
                    }}>
                      {p.status}
                    </div>
                  </div>
                  <button onClick={async (e) => { e.stopPropagation(); if(!window.confirm("Delete "+p.name+"?")) return; await fetch((process.env.REACT_APP_API_URL||"https://api.jaychrisedi.com")+"/api/partners/"+p.id,{method:"DELETE",headers:{Authorization:"Bearer "+token}}); fetchDashboardData(); }} style={{ background:"none", border:"none", color:"#94a3b8", cursor:"pointer", padding:"8px" }}><Trash2 size={18}/></button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Recent Transactions */}
        <div style={{
          backgroundColor: 'rgba(255,255,255,0.95)', borderRadius: '16px', padding: '28px',
          boxShadow: '0 4px 6px rgba(0,0,0,0.07)', border: '1px solid rgba(226,232,240,0.8)', marginBottom: '32px'
        }}>
          <h2 style={{ fontSize: '18px', fontWeight: '800', color: '#0f172a', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Activity size={20} style={{ color: '#10b981' }} /> Recent Transactions
          </h2>
          {(!summary?.recentTransactions || summary.recentTransactions.length === 0) ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
              <FileText size={48} style={{ opacity: 0.3, marginBottom: '12px' }} />
              <p style={{ fontSize: '16px' }}>No transactions yet. Transactions will appear here as EDI documents are processed.</p>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: '0 4px' }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left', padding: '12px 16px', fontSize: '12px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Type</th>
                    <th style={{ textAlign: 'left', padding: '12px 16px', fontSize: '12px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Direction</th>
                    <th style={{ textAlign: 'left', padding: '12px 16px', fontSize: '12px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Partner</th>
                    <th style={{ textAlign: 'left', padding: '12px 16px', fontSize: '12px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>PO Number</th>
                    <th style={{ textAlign: 'left', padding: '12px 16px', fontSize: '12px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Status</th>
                    <th style={{ textAlign: 'left', padding: '12px 16px', fontSize: '12px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Time</th>
                    <th style={{ textAlign: 'center', padding: '12px 16px', fontSize: '12px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>EDI</th>
                  </tr>
                </thead>
                <tbody>
                  {summary.recentTransactions.map(tx => {
                    const statusStyle = getStatusStyle(tx.status);
                    const StatusIcon = statusStyle.icon;
                    return (
                      <tr key={tx.id} style={{ backgroundColor: 'white' }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8fafc'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}>
                        <td style={{ padding: '16px', borderRadius: '8px 0 0 8px' }}>
                          <span style={{
                            padding: '4px 10px', borderRadius: '6px', fontSize: '13px', fontWeight: '700',
                            backgroundColor: getTypeColor(tx.type) + '15', color: getTypeColor(tx.type)
                          }}>
                            {tx.type}
                          </span>
                          <span style={{ fontSize: '12px', color: '#64748b', marginLeft: '8px' }}>{getTypeLabel(tx.type)}</span>
                        </td>
                        <td style={{ padding: '16px' }}>
                          <span style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '13px', fontWeight: '600', color: tx.direction === 'inbound' ? '#3b82f6' : '#10b981' }}>
                            {tx.direction === 'inbound' ? <ArrowDownLeft size={14} /> : <ArrowUpRight size={14} />}
                            {tx.direction}
                          </span>
                        </td>
                        <td style={{ padding: '16px', fontSize: '14px', fontWeight: '600', color: '#0f172a' }}>{tx.partner?.name || '-'}</td>
                        <td style={{ padding: '16px', fontSize: '13px', fontFamily: 'monospace', color: '#475569' }}>{tx.poNumber || '-'}</td>
                        <td style={{ padding: '16px' }}>
                          <span style={{
                            padding: '4px 10px', borderRadius: '6px', fontSize: '12px', fontWeight: '700',
                            backgroundColor: statusStyle.bg, color: statusStyle.color,
                            display: 'inline-flex', alignItems: 'center', gap: '4px'
                          }}>
                            <StatusIcon size={12} /> {statusStyle.text}
                          </span>
                        </td>
                        <td style={{ padding: '16px', fontSize: '13px', color: '#64748b' }}>{formatTime(tx.createdAt)}</td>
                        <td style={{ padding: '16px', textAlign: 'center', borderRadius: '0 8px 8px 0' }}>
                          {tx.rawEdi && (
                            <button onClick={() => setSelectedTx(selectedTx?.id === tx.id ? null : tx)} style={{
                              backgroundColor: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: '6px',
                              padding: '4px 8px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '4px',
                              fontSize: '12px', color: '#475569'
                            }}>
                              <Eye size={12} /> View
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {/* EDI Viewer */}
              {selectedTx && (
                <div style={{
                  marginTop: '16px', padding: '20px', borderRadius: '12px',
                  backgroundColor: '#0f172a', color: '#e2e8f0', fontSize: '13px',
                  overflowX: 'auto', position: 'relative'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px', alignItems: 'center' }}>
                    <span style={{ color: '#10b981', fontWeight: '700', fontSize: '15px' }}>
                      EDI {selectedTx.type} — {selectedTx.partner?.name} — PO: {selectedTx.poNumber}
                    </span>
                    <button onClick={() => setSelectedTx(null)} style={{
                      backgroundColor: 'rgba(255,255,255,0.1)', border: 'none', color: '#94a3b8',
                      borderRadius: '6px', padding: '4px 12px', cursor: 'pointer', fontSize: '12px'
                    }}>Close</button>
                  </div>
                  <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
                    <div style={{ fontSize: '11px', padding: '3px 10px', borderRadius: '4px', background: '#1e3a5f', color: '#60a5fa' }}>ST/SE: Transaction</div>
                    <div style={{ fontSize: '11px', padding: '3px 10px', borderRadius: '4px', background: '#3b2a0a', color: '#f59e0b' }}>ISA/GS: Envelope</div>
                    <div style={{ fontSize: '11px', padding: '3px 10px', borderRadius: '4px', background: '#1a2e1a', color: '#4ade80' }}>PO1/IT1: Line Items</div>
                    <div style={{ fontSize: '11px', padding: '3px 10px', borderRadius: '4px', background: '#1e293b', color: '#e2e8f0' }}>Other Segments</div>
                  </div>
                  {selectedTx.rawEdi ? selectedTx.rawEdi.split('~').filter(s=>s.trim()).map((seg, i) => {
                    const id = seg.trim().split('*')[0];
                    const names = {ISA:'Interchange Header',IEA:'Interchange Trailer',GS:'Group Header',GE:'Group Trailer',ST:'Transaction Header',SE:'Transaction Trailer',BEG:'PO Begin',BAK:'PO Acknowledgment',BSN:'Ship Notice Begin',BIG:'Invoice Begin',PO1:'Line Item',IT1:'Invoice Line',CTT:'Totals',N1:'Name',N3:'Address',N4:'City/State/Zip',PID:'Product Desc',TD5:'Carrier',DTM:'Date/Time',REF:'Reference',HL:'Hierarchy',SN1:'Ship Item',MAN:'Marks/Numbers',TDS:'Total Value',FOB:'Shipping Terms'};
                    const isEnvelope = ['ISA','IEA','GS','GE'].includes(id);
                    const isTx = ['ST','SE'].includes(id);
                    const isLine = ['PO1','IT1','SN1'].includes(id);
                    return (
                      <div key={i} style={{ display: 'flex', gap: '12px', padding: '4px 0', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                        <span style={{ width: '36px', color: isEnvelope ? '#f59e0b' : isTx ? '#60a5fa' : isLine ? '#4ade80' : '#94a3b8', fontWeight: '700', fontFamily: 'monospace', flexShrink: 0 }}>{id}</span>
                        <span style={{ width: '140px', color: '#64748b', fontSize: '11px', flexShrink: 0 }}>{names[id]||''}</span>
                        <span style={{ color: isEnvelope ? '#fbbf24' : isTx ? '#93c5fd' : isLine ? '#86efac' : '#cbd5e1', fontFamily: 'monospace', fontSize: '12px' }}>{seg.trim()}</span>
                      </div>
                    );
                  }) : <p style={{color:'#64748b'}}>No EDI data available</p>}
                </div>
              )}
            </div>
          )}

          {selectedPartner && (
            <div style={{ marginTop: "24px", padding: "24px", borderRadius: "16px", border: "2px solid #10b981", backgroundColor: "white" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
                <h3 style={{ fontSize: "20px", fontWeight: "800", color: "#0f172a", margin: 0 }}>{selectedPartner.name}</h3>
                <button onClick={() => setSelectedPartner(null)} style={{ background: "none", border: "none", cursor: "pointer", color: "#64748b", fontSize: "20px" }}>✕</button>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px", marginBottom: "20px" }}>
                <div style={{ padding: "16px", borderRadius: "10px", backgroundColor: "#f8fafc" }}>
                  <div style={{ fontSize: "12px", color: "#64748b", marginBottom: "4px" }}>EDI Identifier</div>
                  <div style={{ fontSize: "16px", fontWeight: "700", color: "#0f172a", fontFamily: "monospace" }}>{selectedPartner.ediIdentifier}</div>
                </div>
                <div style={{ padding: "16px", borderRadius: "10px", backgroundColor: "#f8fafc" }}>
                  <div style={{ fontSize: "12px", color: "#64748b", marginBottom: "4px" }}>Your EDI ID</div>
                  <div style={{ fontSize: "16px", fontWeight: "700", color: "#0f172a", fontFamily: "monospace" }}>{selectedPartner.yourEdiId}</div>
                </div>
                <div style={{ padding: "16px", borderRadius: "10px", backgroundColor: "#f8fafc" }}>
                  <div style={{ fontSize: "12px", color: "#64748b", marginBottom: "4px" }}>SFTP Host</div>
                  <div style={{ fontSize: "14px", fontWeight: "600", color: "#0f172a", fontFamily: "monospace" }}>{selectedPartner.sftpHost || "Not configured"}</div>
                </div>
                <div style={{ padding: "16px", borderRadius: "10px", backgroundColor: "#f8fafc" }}>
                  <div style={{ fontSize: "12px", color: "#64748b", marginBottom: "4px" }}>Connection</div>
                  <div style={{ fontSize: "14px", fontWeight: "600", color: "#0f172a" }}>{selectedPartner.connectionType?.toUpperCase() || "SFTP"} : {selectedPartner.sftpPort || 22}</div>
                </div>
                <div style={{ padding: "16px", borderRadius: "10px", backgroundColor: "#f8fafc" }}>
                  <div style={{ fontSize: "12px", color: "#64748b", marginBottom: "4px" }}>Inbound Path</div>
                  <div style={{ fontSize: "14px", fontWeight: "600", color: "#0f172a", fontFamily: "monospace" }}>{selectedPartner.inboundPath}</div>
                </div>
                <div style={{ padding: "16px", borderRadius: "10px", backgroundColor: "#f8fafc" }}>
                  <div style={{ fontSize: "12px", color: "#64748b", marginBottom: "4px" }}>Outbound Path</div>
                  <div style={{ fontSize: "14px", fontWeight: "600", color: "#0f172a", fontFamily: "monospace" }}>{selectedPartner.outboundPath}</div>
                </div>
              </div>
              <h4 style={{ fontSize: "14px", fontWeight: "700", color: "#0f172a", marginBottom: "12px" }}>Recent Transactions</h4>
              {(summary?.recentTransactions || []).filter(t => t.partnerId === selectedPartner.id).length > 0 ? (
                (summary?.recentTransactions || []).filter(t => t.partnerId === selectedPartner.id).slice(0,5).map(t => (
                  <div key={t.id} style={{ display: "flex", justifyContent: "space-between", padding: "10px", borderBottom: "1px solid #f1f5f9", fontSize: "13px" }}>
                    <span style={{ color: "#0f172a", fontWeight: "600" }}>{t.type} - {t.direction}</span>
                    <span style={{ color: "#64748b" }}>{t.poNumber || "-"}</span>
                    <span style={{ color: t.status==="completed"?"#10b981":"#f59e0b" }}>{t.status}</span>
                  </div>
                ))
              ) : (
                <p style={{ color: "#94a3b8", fontSize: "13px" }}>No transactions yet for this partner.</p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
